import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  SERVICE_MODE,
  TIME_SLOT_INTERVAL_MINUTES,
} from "./constants";

export const isEmpty = (value) => {
  return value === null || value === undefined || String(value).trim() === "";
};

export const isValidEmail = (email) => {
  if (isEmpty(email)) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

export const isValidPhoneNumber = (phoneNumber) => {
  if (isEmpty(phoneNumber)) return false;

  const normalized = String(phoneNumber).replace(/[^\d]/g, "");
  return normalized.length >= 10 && normalized.length <= 15;
};

export const isStrongEnoughPassword = (password) => {
  if (isEmpty(password)) return false;
  return String(password).length >= 8;
};

export const isValidTimeString = (time) => {
  if (isEmpty(time)) return false;
  return /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.test(String(time).trim());
};

export const normalizeTimeString = (time) => {
  if (!isValidTimeString(time)) return "";

  const [hours, minutes] = String(time).split(":");
  return `${hours}:${minutes}`;
};

export const timeStringToMinutes = (time) => {
  if (!isValidTimeString(time)) return NaN;

  const [hours, minutes] = normalizeTimeString(time).split(":").map(Number);
  return hours * 60 + minutes;
};

export const isTimeIncrementValid = (
  time,
  interval = TIME_SLOT_INTERVAL_MINUTES
) => {
  const minutes = timeStringToMinutes(time);

  if (Number.isNaN(minutes)) return false;
  return minutes % interval === 0;
};

export const isEndTimeAfterStartTime = (startTime, endTime) => {
  const start = timeStringToMinutes(startTime);
  const end = timeStringToMinutes(endTime);

  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end > start;
};

export const calculatePartySize = (numOfAdults = 0, numOfChildren = 0) => {
  const adults = Number(numOfAdults) || 0;
  const children = Number(numOfChildren) || 0;
  return adults + children;
};

export const resolveServiceModeByPartySize = (
  numOfAdults = 0,
  numOfChildren = 0
) => {
  const partySize = calculatePartySize(numOfAdults, numOfChildren);
  return partySize >= AUTO_MOBILE_PARTY_SIZE_THRESHOLD
    ? SERVICE_MODE.MOBILE
    : SERVICE_MODE.STUDIO;
};

export const validateAddress = (address = {}) => {
  const errors = {};

  if (isEmpty(address.line1)) errors.line1 = "Address line 1 is required.";
  if (isEmpty(address.city)) errors.city = "City is required.";
  if (isEmpty(address.state)) errors.state = "State is required.";
  if (isEmpty(address.zip)) errors.zip = "ZIP code is required.";

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateBookingPayload = ({
  bookingDate,
  startTime,
  endTime,
  serviceId,
  hennaType,
  serviceMode,
  numOfAdults = 0,
  numOfChildren = 0,
  guest = null,
  onsiteAddress = null,
}) => {
  const errors = {};

  if (isEmpty(serviceId)) {
    errors.serviceId = "Please select a service.";
  }

  if (isEmpty(hennaType)) {
    errors.hennaType = "Please select a henna type.";
  }

  if (isEmpty(bookingDate)) {
    errors.bookingDate = "Please select a booking date.";
  }

  if (!isValidTimeString(startTime)) {
    errors.startTime = "Please select a valid start time.";
  }

  if (!isValidTimeString(endTime)) {
    errors.endTime = "Please select a valid end time.";
  }

  if (isValidTimeString(startTime) && !isTimeIncrementValid(startTime)) {
    errors.startTime = `Start time must use ${TIME_SLOT_INTERVAL_MINUTES}-minute increments.`;
  }

  if (isValidTimeString(endTime) && !isTimeIncrementValid(endTime)) {
    errors.endTime = `End time must use ${TIME_SLOT_INTERVAL_MINUTES}-minute increments.`;
  }

  if (
    isValidTimeString(startTime) &&
    isValidTimeString(endTime) &&
    !isEndTimeAfterStartTime(startTime, endTime)
  ) {
    errors.endTime = "End time must be after start time.";
  }

  const partySize = calculatePartySize(numOfAdults, numOfChildren);
  if (partySize <= 0) {
    errors.partySize = "At least one person must be included in the booking.";
  }

  const resolvedServiceMode =
    serviceMode || resolveServiceModeByPartySize(numOfAdults, numOfChildren);

  if (resolvedServiceMode === SERVICE_MODE.MOBILE) {
    const addressValidation = validateAddress(onsiteAddress || {});
    if (!addressValidation.isValid) {
      errors.onsiteAddress = addressValidation.errors;
    }
  }

  if (guest) {
    if (isEmpty(guest.firstName)) {
      errors.guestFirstName = "Guest first name is required.";
    }

    if (isEmpty(guest.lastName)) {
      errors.guestLastName = "Guest last name is required.";
    }

    if (!isValidEmail(guest.email)) {
      errors.guestEmail = "A valid guest email is required.";
    }

    if (!isEmpty(guest.phoneNumber) && !isValidPhoneNumber(guest.phoneNumber)) {
      errors.guestPhoneNumber = "Please enter a valid phone number.";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    partySize,
    serviceMode: resolvedServiceMode,
  };
};

const validators = {
  isEmpty,
  isValidEmail,
  isValidPhoneNumber,
  isStrongEnoughPassword,
  isValidTimeString,
  normalizeTimeString,
  timeStringToMinutes,
  isTimeIncrementValid,
  isEndTimeAfterStartTime,
  calculatePartySize,
  resolveServiceModeByPartySize,
  validateAddress,
  validateBookingPayload,
};

export default validators;